/**
 * EmailAttachmentList — presentational list of the attachment chips for one
 * email event. Hides inline IMAGES (already rendered in the email body) and
 * renders one EmailAttachmentChip per remaining entry, using each entry's
 * ORIGINAL position in the `attachments` array as the download-URL index
 * (chips must not renumber after filtering). Renders nothing — no wrapper
 * element — when there are no visible attachments.
 *
 * `content_inlined` alone is not an inline-image marker: the Outlook parser
 * sets it for any attachment at or below the inline-image size cap regardless
 * of content type, so a small non-image (e.g. a 1 MB PDF) carries it too.
 * We therefore hide an entry only when it is inlined AND an image, so those
 * real attachments still surface as chips.
 */

import { memo, useMemo } from 'react';

import type { EmailAttachment } from '../timeline/types';
import { EmailAttachmentChip } from './email-attachment-chip';

export interface EmailAttachmentListProps {
  orgId: number;
  eventId: string;
  attachments: EmailAttachment[] | undefined;
}

export const EmailAttachmentList = memo(function EmailAttachmentList({
  orgId,
  eventId,
  attachments,
}: EmailAttachmentListProps) {
  const renderable = useMemo(
    () =>
      (attachments ?? [])
        .map((att, arrayIndex) => ({
          att,
          arrayIndex,
          chipKey:
            att.id ??
            (att.index != null ? `gmail-${att.index}` : `pos-${arrayIndex}`),
        }))
        .filter(
          ({ att }) =>
            !(att.content_inlined && att.content_type?.startsWith('image/'))
        ),
    [attachments]
  );

  if (renderable.length === 0) {
    return null;
  }

  return (
    <div className='mt-2 flex flex-wrap gap-2'>
      {renderable.map(entry => (
        <EmailAttachmentChip
          key={entry.chipKey}
          orgId={orgId}
          eventId={eventId}
          index={entry.arrayIndex}
          name={entry.att.name}
          size={entry.att.size}
          contentType={entry.att.content_type}
        />
      ))}
    </div>
  );
});
